import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { salonService } from "../../services/salonService";
import { subscriptionService } from "../../services/subscriptionService";
import {
  subscriptionRequestService,
  SUBSCRIPTION_REQUEST_STATUS,
} from "../../services/subscriptionRequestService";
import { settingService } from "../../services/settingService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const STATUS_STYLES = {
  ACTIVE: "bg-success/15 text-success border border-success/20",
  TRIAL: "bg-purple-500/15 text-purple-600 border border-purple-500/20",
  SUSPENDED: "bg-tier-gold/15 text-tier-gold border border-tier-gold/20",
  INACTIVE: "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40",
};

const STATUS_LABELS = {
  ACTIVE: "Aktif Lisans",
  TRIAL: "Deneme Süresi",
  SUSPENDED: "Süresi Doldu / Askıda",
  INACTIVE: "Pasif",
};

export default function MySubscriptionPage() {
  const { showToast } = useToast();
  const user = useSelector((state) => state.auth.user);
  const salonId = user?.salonId;

  const [salon, setSalon] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packageHistory, setPackageHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedMonths, setSelectedMonths] = useState(12);
  const [activeMonths, setActiveMonths] = useState([1, 3, 6, 12]);
  const [submitting, setSubmitting] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const loadData = async () => {
    if (!salonId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [salonRes, packagesRes, historyRes, requestsRes, trialRes, activeMonthsRes] = await Promise.all([
        salonService.get(salonId),
        subscriptionService.list(),
        salonService.getPackageHistory(salonId),
        subscriptionRequestService.listForSalon(salonId),
        settingService.getSetting("default_trial_days", "7"),
        settingService.getSetting("active_license_months", "1,3,6,12"),
      ]);
      const currentSalon = salonRes.data.data;
      setSalon(currentSalon);
      setPackages(packagesRes.data.data ?? []);
      setPackageHistory(historyRes.data.data ?? []);
      setRequests(requestsRes.data.data ?? []);
      const parsedTrial = parseInt(trialRes.data.data, 10);
      setTrialDays(Number.isFinite(parsedTrial) && parsedTrial > 0 ? parsedTrial : 7);

      const monthsStr = activeMonthsRes.data?.data || "1,3,6,12";
      const parsedActiveMonths = monthsStr
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n > 0);
      const validActiveMonths = parsedActiveMonths.length > 0 ? parsedActiveMonths : [1, 3, 6, 12];
      setActiveMonths(validActiveMonths);

      if (!validActiveMonths.includes(selectedMonths)) {
        setSelectedMonths(validActiveMonths[0]);
      }
      setSelectedPackageId(currentSalon?.packageId || "");
    } catch (err) {
      setError(err.response?.data?.error ?? "Abonelik bilgileri yüklenemedi.");
      showToast("Abonelik bilgileri yüklenemedi.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonId]);

  const pendingRequest = requests.find((r) => r.status === "PENDING") || null;

  const handleSubscribeSubmit = (e) => {
    e.preventDefault();
    if (!selectedPackageId || pendingRequest) return;

    const pkg = packages.find((p) => p.id === selectedPackageId);
    if (pkg && pkg.active === false) {
      showToast(`"${pkg.name}" paketi şu anda pasiftir ve yeni yükseltmelere kapalıdır.`, "warning");
      return;
    }
    const months = Number(selectedMonths);
    if (!activeMonths.includes(months)) {
      showToast(`${months} aylık lisans süresi seçeneği şu anda pasiftir.`, "warning");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Lisans Talebi Oluştur",
      message: `"${pkg?.name ?? "Seçilen paket"}" paketi için ${months} aylık lisans talebiniz oluşturulacak. Ödemeniz kontrol edildikten sonra yöneticimiz (admin) tarafından onaylanınca lisansınız aktifleşecektir. Devam edilsin mi?`,
      onConfirm: async () => {
        setSubmitting(true);
        try {
          await subscriptionRequestService.create({
            salonId,
            packageId: selectedPackageId,
            months,
            note: null,
          });
          showToast("Lisans talebiniz alındı. Ödeme onayı sonrası aktifleştirilecektir.", "success");
          await loadData();
        } catch (err) {
          showToast(err.response?.data?.error ?? "İşlem başarısız oldu.", "error");
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
        Yükleniyor…
      </div>
    );
  }

  if (!salonId || error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-2xl p-6 text-error text-center">
        {error || "Salon bilginiz bulunamadı."}
      </div>
    );
  }

  // Calculate days remaining
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = salon.licenseExpiryDate ? new Date(salon.licenseExpiryDate) : null;
  const daysLeft = expiryDate ? Math.round((expiryDate - today) / 86400000) : null;

  const isTrial = salon.status === "TRIAL";
  const isExpired = daysLeft !== null && daysLeft <= 0;

  const selectedPackage = packages.find((p) => p.id === selectedPackageId);
  const totalPrice = selectedPackage ? Number(selectedPackage.monthlyPrice) * Number(selectedMonths) : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="verified_user" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Paketim & Lisansım</h1>
          <p className="text-on-surface-variant text-sm">
            Mevcut abonelik durumunuzu görüntüleyin, test sürenizi uzatın veya 1, 3, 6, 12 aylık paket seçimi yapın.
          </p>
        </div>
      </div>

      {/* Trial / Expiry Banner */}
      {isTrial && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-3xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-2xl bg-purple-500/20 text-purple-600 flex items-center justify-center shrink-0 mt-0.5">
            <Icon name="timer" className="!text-[22px]" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold text-purple-900 text-base">{trialDays} Günlük Ücretsiz Deneme Süresindesiniz!</h3>
            <p className="text-sm text-purple-700 leading-relaxed">
              Sistemi ücretsiz olarak deneyebilirsiniz. Kalan deneme süreniz:{" "}
              <strong>{daysLeft != null ? `${daysLeft} gün` : `${trialDays} gün`}</strong>.
              Kesintisiz kullanıma devam etmek için aşağıdan paketinizi ve lisans sürenizi seçip aktif edebilirsiniz.
            </p>
          </div>
        </div>
      )}

      {isExpired && !isTrial && (
        <div className="bg-error/10 border border-error/20 rounded-3xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-2xl bg-error/20 text-error flex items-center justify-center shrink-0 mt-0.5">
            <Icon name="warning" className="!text-[22px]" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold text-error text-base">Lisans Süreniz Doldu!</h3>
            <p className="text-sm text-error/80 leading-relaxed">
              Sistemi kullanmaya devam edebilmek için lütfen aşağıdaki seçeneklerden uygun abonelik paketini seçerek lisansınızı uzatın.
            </p>
          </div>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <span className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant block mb-1">
            Salon & Lisans No
          </span>
          <h2 className="text-xl font-bold text-on-surface">{salon.name}</h2>
          <p className="text-xs text-primary font-semibold mt-1">Lisans: {salon.licenseNo}</p>
          {salon.taxNumber && <p className="text-xs text-on-surface-variant">Vergi No: {salon.taxNumber}</p>}
        </div>

        <div>
          <span className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant block mb-1">
            Mevcut Durum
          </span>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_STYLES[salon.status] ?? ""}`}>
              {salon.status === "TRIAL"
                ? `${trialDays} Günlük Deneme Süresi`
                : STATUS_LABELS[salon.status] ?? salon.status}
            </span>
          </div>
          <p className="text-xs text-on-surface-variant mt-2">
            Mevcut Paket: <strong>{salon.packageName ?? "—"}</strong>
          </p>
        </div>

        <div>
          <span className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant block mb-1">
            Lisans Bitiş Tarihi
          </span>
          <p className="text-lg font-bold text-on-surface">
            {expiryDate ? expiryDate.toLocaleDateString("tr-TR") : "—"}
          </p>
          <p className="text-xs text-on-surface-variant mt-1">
            {daysLeft !== null
              ? daysLeft > 0
                ? `${daysLeft} gün kaldı`
                : "Süresi doldu"
              : "—"}
          </p>
        </div>
      </div>

      {/* Package Selection Form */}
      <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Icon name="shopping_cart" className="text-primary !text-[22px]" />
          <h2 className="text-lg font-bold text-on-surface">Paket Seçimi ve Lisans Talebi</h2>
        </div>

        <div className="flex items-start gap-2 text-sm text-on-surface-variant bg-surface-container-low border border-outline-variant rounded-2xl px-4 py-3">
          <Icon name="info" className="text-primary !text-[18px] mt-0.5" />
          <p>
            Lisansınızı kendiniz aktifleştiremezsiniz. Paket ve süre seçip <strong>talep oluşturursunuz</strong>;
            ödemeniz elden alınıp kontrol edildikten sonra yöneticimiz (admin) onaylar ve lisansınız aktifleşir.
          </p>
        </div>

        {pendingRequest && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-tier-gold/10 border border-tier-gold/25 rounded-2xl px-4 py-3">
            <div className="flex items-start gap-2">
              <Icon name="hourglass_top" className="text-tier-gold !text-[20px] mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-on-surface">Onay bekleyen talebiniz var</p>
                <p className="text-on-surface-variant">
                  {pendingRequest.packageName} · {pendingRequest.months} ay ·{" "}
                  {Number(pendingRequest.totalPrice).toLocaleString("tr-TR")} ₺ — admin onayı bekleniyor.
                  Onaylanana kadar yeni talep oluşturamazsınız.
                </p>
              </div>
            </div>
            <span className="shrink-0 text-xs font-semibold px-3 py-1 rounded-full bg-tier-gold/20 text-tier-gold self-start">
              {SUBSCRIPTION_REQUEST_STATUS.PENDING.label}
            </span>
          </div>
        )}

        <form onSubmit={handleSubscribeSubmit} className="space-y-6">
          {/* Packages Catalog Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map((pkg) => {
              const isSelected = pkg.id === selectedPackageId;
              return (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPackageId(pkg.id)}
                  className={`cursor-pointer rounded-2xl p-5 border-2 transition-all relative flex flex-col justify-between ${
                    isSelected
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-outline-variant hover:border-primary/50 bg-white"
                  }`}
                >
                  {isSelected && (
                    <span className="absolute top-3 right-3 bg-primary text-white p-1 rounded-full text-xs">
                      <Icon name="check" className="!text-[16px]" />
                    </span>
                  )}
                  <div>
                    <h3 className="font-bold text-lg text-on-surface">{pkg.name}</h3>
                    <p className="text-2xl font-extrabold text-primary mt-2">
                      {Number(pkg.monthlyPrice).toLocaleString("tr-TR")} ₺
                      <span className="text-xs font-normal text-on-surface-variant"> / ay</span>
                    </p>
                    <p className="text-xs text-on-surface-variant mt-2 leading-relaxed">
                      {pkg.description || "Tüm temel güzellik salonu yönetim özellikleri dahil."}
                    </p>
                    {pkg.active === false && (
                      <div className="mt-3 bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-bold px-2.5 py-1 rounded-xl flex items-center gap-1.5">
                        <Icon name="block" className="!text-[14px]" />
                        <span>Bu paket yeni başvurulara kapalıdır (Pasif)</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* License Duration Options */}
          <div className="bg-surface-container-low rounded-2xl p-5 space-y-3 border border-outline-variant">
            <label className="block text-sm font-bold text-on-surface">
              Kullanmak İstediğiniz Lisans Süresi
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { months: 1, label: "1 Aylık", badge: "Aylık Ödeme" },
                { months: 3, label: "3 Aylık", badge: "3 Ay Avantajı" },
                { months: 6, label: "6 Aylık", badge: "Popüler" },
                { months: 12, label: "12 Aylık (1 Yıl)", badge: "En Avantajlı" },
              ].map((opt) => {
                const active = Number(selectedMonths) === opt.months;
                const isOptionEnabled = activeMonths.includes(opt.months);
                return (
                  <button
                    type="button"
                    key={opt.months}
                    onClick={() => {
                      if (!isOptionEnabled) {
                        showToast(`${opt.label} lisans süresi seçeneği şu an için pasiftir.`, "warning");
                        return;
                      }
                      setSelectedMonths(opt.months);
                    }}
                    className={`p-4 rounded-xl border text-center transition-all relative ${
                      !isOptionEnabled
                        ? "border-rose-200 bg-rose-50/50 text-rose-400 opacity-75"
                        : active
                        ? "border-primary bg-primary text-white font-bold shadow"
                        : "border-outline-variant bg-white text-on-surface hover:border-primary/50"
                    }`}
                  >
                    <span className="block text-sm font-bold">{opt.label}</span>
                    <span
                      className={`text-[10px] block mt-1 px-2 py-0.5 rounded-full inline-block ${
                        !isOptionEnabled
                          ? "bg-rose-100 text-rose-700 font-bold"
                          : active
                          ? "bg-white/20 text-white"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      {!isOptionEnabled ? "Pasif" : opt.badge}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary & Order Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-primary/5 border border-primary/15 rounded-2xl p-5">
            <div>
              <p className="text-xs text-on-surface-variant uppercase tracking-wider font-semibold">
                Hesaplanan Toplam Tutar ({selectedMonths} Ay):
              </p>
              <p className="text-2xl font-extrabold text-primary mt-0.5">
                {totalPrice.toLocaleString("tr-TR")} ₺
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting || !selectedPackageId || !!pendingRequest}
              title={pendingRequest ? "Onay bekleyen talebiniz varken yeni talep oluşturamazsınız." : undefined}
              className="w-full sm:w-auto bg-primary text-white rounded-xl px-8 py-3.5 font-bold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {pendingRequest ? (
                <><Icon name="hourglass_top" /> Onay Bekleniyor</>
              ) : submitting ? (
                "Talep gönderiliyor…"
              ) : (
                <><Icon name="send" /> Paketi Seç & Lisans Talebi Oluştur</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Package History */}
      <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6 space-y-4">
        <h3 className="font-bold text-base text-on-surface flex items-center gap-2">
          <Icon name="history" className="text-primary" /> Geçmiş Paket ve Lisans Hareketleri
        </h3>
        <div className="border border-outline-variant rounded-2xl overflow-hidden">
          <table className="w-full text-xs text-left">
            <thead className="bg-surface-container-low text-on-surface-variant font-semibold border-b border-outline-variant">
              <tr>
                <th className="px-4 py-3">Paket</th>
                <th className="px-4 py-3">Başlangıç</th>
                <th className="px-4 py-3">Bitiş</th>
                <th className="px-4 py-3 text-right">Aylık Fiyat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant bg-white">
              {packageHistory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center text-on-surface-variant">
                    Geçmiş paket hareketi bulunamadı.
                  </td>
                </tr>
              ) : (
                packageHistory.map((hist) => (
                  <tr key={hist.id} className="hover:bg-primary/5 transition-colors">
                    <td className="px-4 py-3 font-semibold text-on-surface">
                      {hist.packageName}
                      {!hist.endDate && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] bg-success/15 text-success border border-success/20 rounded-full">
                          Aktif Paket
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant">
                      {new Date(hist.startDate).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant">
                      {hist.endDate ? new Date(hist.endDate).toLocaleDateString("tr-TR") : "Devam Ediyor"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-primary">
                      {hist.price != null ? `${Number(hist.price).toLocaleString("tr-TR")} ₺` : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type="info"
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
